import { useTranslate } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Card, Empty, Flex, Space, Table, Tag, Typography, theme } from 'antd';
import type {
  ProfileCollectionOut,
  ProfileOut,
  PublicationListOut,
} from '@/api/generated/schemas';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { formatDateTime } from '@/utils/format';

/** One profile collection as its own card, with a server-side paginated
 * table of its publications. */
const ProfileCollectionCard = ({
  collection,
}: {
  collection: ProfileCollectionOut;
}) => {
  const t = useTranslate();
  const publicationIds = collection.publication_ids ?? [];
  const hasIds = publicationIds.length > 0;
  const { tableProps, result } = useTable<PublicationListOut>({
    resource: 'user_catalog.publications',
    queryOptions: { enabled: hasIds },
    filters: {
      permanent: [{ field: 'id', operator: 'in', value: publicationIds }],
    },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  return (
    <Card
      title={
        <Space>
          {collection.name}
          <Tag>{t(`account.profile_collections.kind.${collection.kind}`)}</Tag>
          <Typography.Text type="secondary">
            {`${t('account.profile_collections.publications.section')} (${result?.total ?? publicationIds.length})`}
          </Typography.Text>
        </Space>
      }
      extra={
        <Typography.Text type="secondary">
          {`${t('base.fields.id')}: ${collection.id} · ${formatDateTime(collection.created_at)}`}
        </Typography.Text>
      }
    >
      {/* Disabled query never becomes `isFetched`, so refine's
          `tableProps.loading` (= !isFetched) would spin forever. */}
      <Table<PublicationListOut>
        {...tableProps}
        loading={hasIds ? tableProps.loading : false}
        dataSource={hasIds ? tableProps.dataSource : []}
        rowKey="id"
        size="small"
      >
        <Table.Column<PublicationListOut>
          dataIndex="id"
          title={t('base.fields.id')}
          width={80}
          render={(value: number, row) => (
            <PublicationInfoPopover publication={row} publicationId={value}>
              {value}
            </PublicationInfoPopover>
          )}
        />
        <Table.Column<PublicationListOut>
          dataIndex="title"
          title={t('account.profile_collections.publications.title')}
          render={(_: string, row) => (
            <PublicationInfoPopover publication={row} publicationId={row.id} />
          )}
        />
      </Table>
    </Card>
  );
};

export const ProfileCollectionsTab = ({ record }: { record: ProfileOut }) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const collections = record.collections ?? [];

  if (collections.length === 0) {
    return (
      <Card title={`${t('account.profiles.tabs.collections')} (0)`}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    );
  }

  return (
    <Flex vertical gap={token.margin}>
      {collections.map((collection) => (
        <ProfileCollectionCard key={collection.id} collection={collection} />
      ))}
    </Flex>
  );
};
